import sanitizeHtml from "sanitize-html";
import blockquoteNormaliser from "./blockquote-normalizer";
import brRemover from "./br-remover";
import commentRemover from "./comment-remover";
import divNormaliser from "./div-normalizer";
import { getPhrasingContentSchema, removeInvalidHTML } from "./dom";
import emptyParagraphRemover from "./empty-paragraph-remover";
import figureContentReducer from "./figure-content-reducer";
import googleDocsUIdRemover from "./google-docs-uid-remover";
import headRemover from "./head-remover";
import htmlFormattingRemover from "./html-formatting-remover";
import { htmlToBlocks } from "./html-to-blocks";
import iframeRemover from "./iframe-remover";
import imageCorrector from "./image-corrector";
import isInlineContent from "./is-inline-content";
import listReducer from "./list-reducer";
import markdownConverter from "./markdown-converter";
import msListConverter from "./ms-list-converter";
import normalizeBlocks from "./normalize-blocks";
import phrasingContentReducer from "./phrasing-content-reducer";
import slackParagraphCorrector from "./slack-paragraph-corrector";
import specialCommentConverter from "./special-comment-converter";
import { deepFilterHTML, getBlockContentSchema, isPlain } from "./utils";

/**
 * Filters HTML to only contain phrasing content.
 *
 * @param {string}  HTML The HTML to filter.
 * @param {boolean} preserveWhiteSpace Whether or not to preserve consequent white space.
 *
 * @return {string} HTML only containing phrasing content.
 */
function filterInlineHTML(HTML) {
  HTML = deepFilterHTML(HTML, [
    googleDocsUIdRemover,
    phrasingContentReducer,
    commentRemover,
  ]);
  HTML = removeInvalidHTML(HTML, getPhrasingContentSchema("paste"), {
    inline: true,
  });

  HTML = deepFilterHTML(HTML, [htmlFormattingRemover, brRemover]);

  return HTML;
}

export default function rawPasteHandler(
  html,
  plainText,
  tagName,
  isInlineString = false,
) {
  // First of all, strip any meta tags.
  html = html.replace(/<meta[^>]+>/g, "");
  // Strip Windows markers.
  html = html.replace(
    /^\s*<html[^>]*>\s*<body[^>]*>(?:\s*<!--\s*StartFragment\s*-->)?/i,
    "",
  );
  html = html.replace(
    /(?:<!--\s*EndFragment\s*-->\s*)?<\/body>\s*<\/html>\s*$/i,
    "",
  );

  // Check plain text if there is no HTML.
  const content = html ? html : plainText;

  if (plainText && (!html || isPlain(html))) {
    html = plainText;

    // The markdown converter (Showdown) trims whitespace.
    if (!/^\s+$/.test(plainText)) {
      html = markdownConverter(html);
    }

    // Switch to inline mode if:
    // * The current mode is AUTO.
    // * The original plain text had no line breaks.
    // * The original plain text was not an HTML paragraph.
    // * The converted text is just a paragraph.
    if (
      plainText.indexOf("\n") === -1 &&
      plainText.indexOf("<p>") !== 0 &&
      html.indexOf("<p>") === 0
    ) {
      isInlineString = true;
    }
  }

  if (tagName === "code") {
    return plainText;
  }

  if (isInlineString) {
    return filterInlineHTML(html);
  }

  // Belongs to same tag
  if (isInlineContent(html, tagName)) {
    return filterInlineHTML(html);
  }

  // Normalize unicode to use composed characters.
  // This is unsupported in IE 11 but it's a nice-to-have feature, not mandatory.
  // Not normalizing the content will only affect older browsers and won't
  // entirely break the app.
  // See: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/normalize
  // See: https://core.trac.wordpress.org/ticket/30130
  // See: https://github.com/WordPress/gutenberg/pull/6983#pullrequestreview-125151075
  if (String.prototype.normalize) {
    html = html.normalize();
  }

  // Parse Markdown (and encoded HTML) if:
  // * There is a plain text version.
  // * There is no HTML version, or it has no formatting.
  if (plainText && (!html || isPlain(html))) {
    html = plainText;

    // The markdown converter (Showdown) trims whitespace.
    if (!/^\s+$/.test(plainText)) {
      html = markdownConverter(html);
    }
  }

  // Must be run before checking if it's inline content.
  html = deepFilterHTML(html, [slackParagraphCorrector]);

  // Needs to parse for Grigora Copy first here.
  const filters = [
    googleDocsUIdRemover,
    msListConverter,
    headRemover,
    listReducer,
    imageCorrector,
    phrasingContentReducer,
    specialCommentConverter,
    commentRemover,
    iframeRemover,
    figureContentReducer,
    blockquoteNormaliser,
    divNormaliser,
  ];

  const blockContentSchema = getBlockContentSchema("paste");
  const phrasingContentSchema = getPhrasingContentSchema("paste");

  const schema = {
    ...blockContentSchema,
    ...phrasingContentSchema,
  };

  html = deepFilterHTML(html, filters, blockContentSchema);
  html = removeInvalidHTML(html, schema);
  html = normalizeBlocks(html);
  html = deepFilterHTML(
    html,
    [htmlFormattingRemover, brRemover, emptyParagraphRemover],
    blockContentSchema,
  );

  var blocks = htmlToBlocks(html, rawPasteHandler);

  return blocks;
}
